import type { AxiosError } from 'axios'

import { isCartOperationInProgressError } from '@/lib/cartErrors'

/**
 * Counts Sunny16's busy-cart refusals: the 409 `CartOperationInProgressException` returned when
 * another request holds the cart.
 *
 * **Why this exists.** The refusal is an expected error (`_expectedError` on every cart-mutating
 * call), so it never reaches New Relic's error rate. Without this, nothing would show how often
 * customers meet a locked cart once the lock ships. Each refusal emits one `PageAction`:
 *
 * ```
 * SELECT count(*) FROM PageAction WHERE actionName = 'cartBusyRefusal'
 * FACET reason, method, endpoint SINCE 1 week ago
 * ```
 *
 * Like `cartUpsertTelemetry.ts`, deliberately not gated on `VITE_REPORT_ERRORS_TO_NEW_RELIC`: a
 * `PageAction` is query-only data, and demo is the first place the numbers will be wanted. `mode`
 * separates demo from local dev, which report to the same application.
 */

/** New Relic `PageAction` name — the `actionName` to filter on in NRQL. */
export const CART_BUSY_REFUSAL_ACTION = 'cartBusyRefusal'

// Path segments kept as-is in `endpoint`. Everything else — cart and bundle ids, the cart's
// security key, quantities — becomes `:param`, so the attribute groups by route and never
// carries a cart credential. A route word missing here only shows as `:param`.
const ROUTE_WORDS = new Set([
  'api',
  'storefront',
  'cart',
  'config',
  'customizations',
  'promotions',
  'prepare',
  'order',
  'adjust',
  'checkout',
  'customerservice',
  'begin-payment',
  'release-payment'
])

/** Emits the `PageAction` for a busy-cart refusal; does nothing for any other error. */
export function reportCartBusyRefusal(error: unknown): void {
  if (!isCartOperationInProgressError(error)) return

  const busy = error as AxiosError<{ reason?: string }>
  const attributes = {
    reason: busy.response?.data?.reason ?? 'unknown',
    method: busy.config?.method?.toUpperCase() ?? 'unknown',
    endpoint: endpointOf(busy),
    mode: import.meta.env.MODE
  }

  if (import.meta.env.DEV) {
    console.debug(`[${CART_BUSY_REFUSAL_ACTION}]`, attributes)
  }

  window.newrelic?.addPageAction(CART_BUSY_REFUSAL_ACTION, attributes)
}

function endpointOf(error: AxiosError): string {
  const path = `${error.config?.baseURL ?? ''}${error.config?.url ?? ''}`
    .replace(/^[a-z]+:\/\/[^/]+/i, '')
    .split('?')[0]

  return path
    .split('/')
    .map((segment) => (segment === '' || ROUTE_WORDS.has(segment) ? segment : ':param'))
    .join('/')
}
